// ─── Sous-module « core-profil » ──────────────────────────────────
// L'écran Profil COMMUN à toutes les apps de la suite : compte, ESPACES (le
// tenant), apparence, langue, à propos. C'est aussi le point d'ancrage de
// l'espace — c'est ici qu'on le crée, le renomme et qu'on bascule de l'un à
// l'autre.
//
// EXTENSION PAR L'APP (le « cal-core-profil » de MoPCal) : une app injecte ses
// sections propres via la prop `sections`, sans que le Core connaisse quoi que
// ce soit de leur contenu :
//
//   <EcranProfil sections={[
//     { cle:'calendriers', titre:'Mes calendriers', emoji:'📅',
//       sousTitre:'3 calendriers', rendu: <SectionCalendriers /> },
//   ]} />
//
// La page est structurée en DEUX blocs, contrat stable de la suite :
//   • « Gestion spécifique » — emplacement RÉSERVÉ par le Core, rempli par
//     l'app via `sections` (ordre : `position: 'haut'` d'abord, puis le reste).
//     Masqué si l'app n'injecte rien.
//   • « Gestion générique » — espaces, membres, Qui voit quoi, apparence,
//     langue, à propos : identique dans TOUTES les apps.
//   • « Gestion autre » — outils transverses : calendriers IVV (valideurs),
//     activation des fonctions. Masqué si aucun n'est disponible.
// Exception : une section marquée `acces: true` n'est pas rendue dans le bloc
// spécifique mais DANS « Qui voit quoi » (la gestion des droits est générique).
// Le contrat est volontairement minimal : un titre, un emoji, un nœud React.
import React, { useEffect, useMemo, useState } from 'react';
import {
  View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet, Modal,
} from 'react-native';
import { useUI, usePreferencesUI } from '../theme/PreferencesUI';
import { useAuth } from '../auth/AuthContext';
import BasculeComptes from '../auth/BasculeComptes';
import { useEspace } from '../espace/EspaceContext';
import { useApp } from '../app/AppContext';
import { useLangue } from '../i18n/LangueContext';
import { LANGUES } from '../i18n/i18n';
import {
  renommerEspace, supprimerEspace, roleDansEspace,
  inviterMembre, annulerInvitation, retirerMembre, definirAccesDeclareN, accesDeclareN,
} from '../espace/espaces';
import { membresDe, estProprietaire } from '../acces/acces';
import SectionAcces from '../acces/SectionAcces';
import SectionMonForfait from '../forfaits/SectionMonForfait';
import SectionALaCarte from '../forfaits/SectionALaCarte';
import { usePeutUtiliser } from '../forfaits/FonctionsContext';
import { jetonsDuPlan } from '../forfaits/jetons';
import useQuotaEspaces from '../forfaits/useQuotaEspaces';
import Stepper from '../ui/Stepper';
import { ecouterPlans, nomPlan } from '../forfaits/plansConfig';
import { FormAccesProvider } from '../acces/FormAccesContext';
import { confirmerSuppression, confirmerChoix } from '../services/confirm';
import { demanderSuppressionCompte } from '../auth/compteService';
import { libelleProjet } from '../firebase.config';
import { appInfo } from '../modules';
import CarteInfos from '../ui/CarteInfos';
import { versionAuMoins } from '../version';
import { ecouterDerniereVersion } from '../services/config';
import { CORE_VERSION } from '../version';
import PageHeader from '../ui/PageHeader';
import Feuille from '../ui/Feuille';
import LigneSwipe from '../ui/LigneSwipe';
import SectionPliable from '../ui/SectionPliable';
import Bascule from '../ui/Bascule';
import FeuilleCalIvv from './FeuilleCalIvv';
import FeuilleParametresCompte from './FeuilleParametresCompte';
import { doc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import SelecteurEmoji from '../ui/SelecteurEmoji';
import FeuilleFonctions from '../forfaits/FeuilleFonctions';
import FeuilleMesElements from '../forfaits/FeuilleMesElements';
import FicheEspace from '../espace/FicheEspace';
import { useFonctions } from '../forfaits/FonctionsContext';
import { useResetOnglet } from '../nav/MenuOngletContext';

const THEMES = [
  { id: 'auto',  labelKey: 'apparence.auto',   emoji: '🌗' },
  { id: 'light', labelKey: 'apparence.clair',  emoji: '☀️' },
  { id: 'dark',  labelKey: 'apparence.sombre', emoji: '🌙' },
];

// `elementsSources` : les éléments gérables déclarés par l'APP (calendriers,
// inventaires…) — le Core ne connaît pas ces collections.
export default function EcranProfil({ sections = [], elementsSources = [], ongletCle = 'Profil' }) {
  const UI = useUI();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const { user, deconnexion, estIVV } = useAuth();
  // Porte de forfait : sans « Multi-comptes », on ne bascule pas de compte.
  // Une fonction declaree au catalogue DOIT avoir sa porte : sans elle, la
  // couper dans « Fonctions & activation » ne produit aucun effet.
  const peut = usePeutUtiliser();
  const peutComptes   = peut('comptes');
  const peutEspaces   = peut('espaces');
  const peutMembres   = peut('membres');
  const peutApparence = peut('apparence');
  const peutLangue    = peut('langue');
  // Nom du forfait : piloté par l'administration (config/plans), pas figé.
  const [plansCfg, setPlansCfg] = useState({});
  useEffect(() => ecouterPlans(setPlansCfg), []);
  // Dotation de jetons : lue dans la config des fonctionnalités (par plan).
  const [cfgFonctions, setCfgFonctions] = useState({ plans: {} });
  useEffect(() => onSnapshot(doc(db, 'config', 'fonctionnalites'),
    (snap) => setCfgFonctions(snap.exists() ? { plans: snap.data().plans || {} } : { plans: {} }),
    () => {}), []);
  const { espaces, espaceActif, choisirEspace, creerEspace } = useEspace();
  // Seuls les espaces que JE possède sont affichés (un membre ne « voit » pas l'espace).
  const mesEspacesPossedes = useMemo(() => (espaces || []).filter(e => roleDansEspace(e, user?.uid) === 'proprietaire'), [espaces, user?.uid]);
  // Combien d'espaces j'ai le droit de POSSEDER (1 par defaut, reglable par
  // plan dans l'administration). Un espace n'occupe pas de jeton : c'est le
  // contenant, il se compte a part.
  const quotaEspaces = useQuotaEspaces();
  const { theme, setTheme } = usePreferencesUI();
  const langueCtx = useLangue() || {};
  const t = langueCtx.t || ((k) => k);
  const app = useApp();
  // Etat de version : meme source que le pied de page (config/version), pour ne
  // pas afficher deux verites differentes dans la meme app.
  const [derniereVersion, setDerniereVersion] = useState(null);
  useEffect(() => ecouterDerniereVersion(app.id, setDerniereVersion), [app.id]);
  const majDispo = derniereVersion ? !versionAuMoins(app.version, derniereVersion) : null;
  // Canal et bundle OTA : `expo-updates` n'existe pas sur le web ni dans tous
  // les builds — require PROTEGE, absent = ligne simplement non affichee.
  const infoOTA = useMemo(() => {
    try {
      const U = require('expo-updates');
      return {
        canal: U.channel || '',
        bundle: U.updateId ? String(U.updateId).slice(0, 8) : (U.isEmbeddedLaunch ? 'embarque' : ''),
      };
    } catch { return { canal: '', bundle: '' }; }
  }, []);

  // Tous les accordéons REPLIÉS à l'ouverture : la page tient à l'écran et on
  // déplie ce qu'on cherche (un seul ouvert à la fois).
  const [ouvert, setOuvert] = useState(null);
  const [err, setErr] = useState('');
  const [bascule, setBascule] = useState(false);   // sélecteur multi-comptes
  const [parametresCompte, setParametresCompte] = useState(false);   // nom d'utilisateur, etc.
  const [fonctionsSheet, setFonctionsSheet] = useState(false);   // activation des fonctions
  const [elementsSheet, setElementsSheet] = useState(false);     // « Mes éléments » (jetons)
  const [ficheEspace, setFicheEspace] = useState(null);          // fiche info d'un espace
  const [calIvvSheet, setCalIvvSheet] = useState(false);         // choix des calendriers IVV
  const { defs: fonctionsDefs = [] } = useFonctions();

  // Membres & partage.
  const membres  = useMemo(() => membresDe(espaceActif), [espaceActif]);
  const suisProprio = estProprietaire(espaceActif, user?.uid);
  const ownerUid = espaceActif?.creePar || user?.uid;
  const invitations = espaceActif?.invitations || [];
  const [inviteMail, setInviteMail] = useState('');

  async function inviter() {
    const mail = inviteMail.trim();
    if (!mail || !espaceActif) return;
    try { await inviterMembre(espaceActif.id, mail); setInviteMail(''); }
    catch (e) { setErr(e?.message || String(e)); }
  }
  async function definirDeclare(cle, accesN) {
    if (!espaceActif) return;
    try { await definirAccesDeclareN(espaceActif.id, cle, accesN); }
    catch (e) { setErr(e?.message || String(e)); }
  }

  // Feuille d'édition d'un espace (création ou renommage).
  const [sheet, setSheet] = useState(null);   // { id|null, nom, emoji }
  const [busy, setBusy]   = useState(false);
  const [suppBusy, setSuppBusy] = useState(false);

  function supprimerMonCompte() {
    confirmerSuppression(t('profil.supprimerMonCompteTitre'), t('profil.supprimerMonCompteMsg'), async () => {
      setSuppBusy(true);
      try { await demanderSuppressionCompte(); deconnexion?.(); }
      catch (e) { setErr(e?.message || String(e)); setSuppBusy(false); }
    }, { texteConfirmer: t('commun.supprimer') });
  }

  // Clic sur l'onglet Profil → ferme les feuilles et revient à la page Profil.
  useResetOnglet(ongletCle, () => { setSheet(null); setBascule(false); setFonctionsSheet(false); setElementsSheet(false); setCalIvvSheet(false); });

  // ── Contrat de composition (Core ↔ app) ──
  // Le Core RÉSERVE l'emplacement « Gestion spécifique » ; l'app le remplit via
  // `sections` (ordre : position 'haut' d'abord, puis le reste). Tout le bloc
  // « Gestion générique » qui suit vient du Core et est identique dans chaque
  // app. Les sections marquées `acces` échappent à ce flux : elles sont rendues
  // DANS « Qui voit quoi » (la gestion des droits est générique).
  const specifiques = [
    ...sections.filter(x => !x.acces && x.position === 'haut'),
    ...sections.filter(x => !x.acces && x.position !== 'haut'),
  ];

  const modifieEspace = !!sheet && !!sheet.nom.trim()
    && (!sheet.id || sheet.nom.trim() !== (sheet.nomInitial || '') || sheet.emoji !== (sheet.emojiInitial || ''));

  async function enregistrerEspace() {
    if (!sheet || !modifieEspace || busy) return;
    // Masquer le ＋ ne suffit pas : la feuille peut etre restee ouverte, ou un
    // espace avoir ete cree entre-temps sur un autre appareil.
    if (!sheet.id && !quotaEspaces.peutCreer) {
      setErr(t('profil.espacesMaxAtteint', { n: quotaEspaces.possedes, max: quotaEspaces.max }));
      return;
    }
    setBusy(true); setErr('');
    try {
      if (sheet.id) await renommerEspace(sheet.id, sheet.nom.trim(), sheet.emoji);
      else          await creerEspace(sheet.nom.trim(), sheet.emoji);
      setSheet(null);
    } catch (e) { setErr(e?.message || String(e)); }
    setBusy(false);
  }

  function retirerEspace(e) {
    confirmerSuppression(
      t('profil.supprEspaceTitre'),
      t('profil.supprEspaceMsg', { nom: e.nom }),
      async () => { try { await supprimerEspace(e.id); } catch (x) { setErr(x?.message || String(x)); } },
    );
  }

  const rendreSection = (x) => (
    <SectionPliable
      key={x.cle} titre={x.titre} emoji={x.emoji} sousTitre={x.sousTitre}
      ouvert={ouvert === x.cle} onToggle={() => setOuvert(o => (o === x.cle ? null : x.cle))}>
      {x.rendu}
    </SectionPliable>
  );

  return (
    <FormAccesProvider>
    <View style={s.page}>
      <PageHeader titre={t('nav.profil')} iconeTitre="👤">
        {/* Se déconnecter : action GLOBALE → icône en en-tête. Le ➕ « Nouvel
            espace » ne concerne QUE la section « Mes espaces » : il y vit. */}
        <TouchableOpacity hitSlop={HIT} accessibilityLabel={t('profil.seDeconnecter')} onPress={() => deconnexion?.()}>
          <Text style={s.hIcDanger}>⏻</Text>
        </TouchableOpacity>
      </PageHeader>
      {/* Identite FIXE (hors defilement) : avec le multi-comptes, savoir sur quel
          compte on agit ne doit jamais dependre de la position du scroll.
          Tap = selecteur de comptes ; hors forfait le ⇄ est grise et inerte. */}
      <View style={s.identiteFixe}>
        <TouchableOpacity style={s.identiteInfos} activeOpacity={0.7}
          onPress={() => setParametresCompte(true)} accessibilityLabel={t('profil.parametresCompte')}>
          <View style={s.avatar}><Text style={s.avatarTxt}>{(user?.prenom || user?.email || '?')[0].toUpperCase()}</Text></View>
          <View style={{ flex: 1 }}>
            <Text style={s.nom} numberOfLines={1}>{user?.prenom || user?.email || t('profil.monCompte')}</Text>
            <Text style={s.mail} numberOfLines={1}>{user?.email}</Text>
          </View>
        </TouchableOpacity>
        {/* SEULE cette flèche bascule entre comptes — le reste de la ligne ouvre
            les paramètres du compte (nom d'utilisateur…), pas la même chose. */}
        <TouchableOpacity hitSlop={HIT} disabled={!peutComptes}
          onPress={() => setBascule(true)} accessibilityLabel={t('profil.changerCompte')}>
          <Text style={[s.bascIc, !peutComptes && s.bascIcOff]}>{peutComptes ? '⇄' : '🔒'}</Text>
        </TouchableOpacity>
      </View>
      <ScrollView contentContainerStyle={s.corps} keyboardShouldPersistTaps="handled">
        {!!err && (
          <TouchableOpacity style={s.err} onPress={() => setErr('')}>
            <Text style={s.errTxt}>⚠️ {err}</Text>
          </TouchableOpacity>
        )}

        {/* ── Gestion SPÉCIFIQUE à l'application : emplacement RÉSERVÉ par le
            Core, rempli par l'app via `sections` (calendriers, inventaires,
            apprentissage…). Masqué si l'app n'injecte rien. ── */}
        {specifiques.length > 0 && <Text style={s.titreGroupe}>{t('profil.gestionSpecifique')}</Text>}
        {specifiques.map(rendreSection)}

        {/* ── Gestion GÉNÉRIQUE (Core : espaces, membres, droits, apparence,
            langue, à propos — identique dans toutes les apps de la suite) ── */}
        <Text style={s.titreGroupe}>{t('profil.gestionGenerique')}</Text>

        {/* ── Mes espaces : SEULS ceux que je POSSÈDE ────────────────
            Un membre ne voit pas l'espace : il accède aux ÉLÉMENTS (calendriers…)
            qu'un propriétaire partage avec lui. Seul le propriétaire voit/configure
            l'espace ici. */}
        {peutEspaces && <SectionPliable
          titre={t('profil.mesEspaces')} emoji="🏠"
          sousTitre={mesEspacesPossedes.length ? t(mesEspacesPossedes.length > 1 ? 'profil.nEspacesPluriel' : 'profil.nEspacesSingulier', { n: mesEspacesPossedes.length }) : t('profil.aucunEspacePossede')}
          ouvert={ouvert === 'espaces'} onToggle={() => setOuvert(o => (o === 'espaces' ? null : 'espaces'))}
          action={quotaEspaces.peutCreer ? (
            <TouchableOpacity hitSlop={HIT} accessibilityLabel={t('profil.nouvelEspace')}
              onPress={() => { setOuvert('espaces'); setSheet({ id: null, nom: '', emoji: '🏠', nomInitial: '', emojiInitial: '🏠' }); }}>
              <Text style={s.hIc}>＋</Text>
            </TouchableOpacity>
          ) : null}>
          <Text style={s.aide}>
            {t('profil.espacesAideAvant')} <Text style={{ fontWeight: '800' }}>{t('profil.proprietaireMot')}</Text> {t('profil.espacesAideApres')}
          </Text>
          {/* Compteur toujours visible : on dit ou on en est, et pourquoi le
              ＋ a disparu le cas echeant. */}
          <Text style={s.aide}>{t('profil.espacesCompteur', { n: quotaEspaces.possedes, max: quotaEspaces.max })}</Text>
          {quotaEspaces.atteint && <Text style={s.aide}>{t('profil.espacesMaxAtteint', { n: quotaEspaces.possedes, max: quotaEspaces.max })}</Text>}
          {mesEspacesPossedes.length === 0 && <Text style={s.vide}>{t('profil.aucunEspaceAide')}</Text>}
          {mesEspacesPossedes.map(e => {
            const actif = espaceActif?.id === e.id;
            const proprio = roleDansEspace(e, user?.uid) === 'proprietaire';
            const editer = () => setSheet({ id: e.id, nom: e.nom, emoji: e.emoji || '🏠', nomInitial: e.nom, emojiInitial: e.emoji || '🏠' });
            // Web : le swipe n'est pas fiable → l'appui long propose Éditer/Supprimer.
            const menuWeb = () => confirmerChoix(e.nom, t('profil.queFaireEspace'), [
              { text: t('commun.editer'), onPress: editer },
              { text: t('commun.supprimer'), style: 'destructive', onPress: () => retirerEspace(e) },
              { text: t('commun.annuler'), style: 'cancel' },
            ]);
            return (
              <LigneSwipe key={e.id} espacement={7} enabled={proprio}
                onEditer={proprio ? editer : undefined}
                onSupprimer={proprio ? () => retirerEspace(e) : undefined}>
                <TouchableOpacity style={[s.ligne, { marginBottom: 0 }, actif && s.ligneOn]} activeOpacity={0.7}
                  onPress={() => setFicheEspace(e)} onLongPress={proprio ? menuWeb : undefined}>
                  <View style={s.ligneTxt}>
                    <Text style={s.ligneEmoji}>{e.emoji || '🏠'}</Text>
                    <View style={{ flex: 1 }}>
                      <Text style={s.ligneNom} numberOfLines={1}>{e.nom}</Text>
                      <Text style={s.ligneSous}>
                        {t((e.membreUids || []).length > 1 ? 'profil.nMembresPluriel' : 'profil.nMembresSingulier', { n: (e.membreUids || []).length })}
                        {proprio ? ` · ${t('profil.proprietaireMot')}` : ''}{actif ? ` · ${t('profil.actifMin')}` : ''}
                      </Text>
                    </View>
                  </View>
                </TouchableOpacity>
              </LigneSwipe>
            );
          })}
          <Text style={s.aideMini}>{t('profil.espacesAideMini')}</Text>
        </SectionPliable>}

        {/* ── Membres de l'espace ────────────────────────────────── */}
        {peutMembres && !!espaceActif && (
          <SectionPliable
            titre={t('profil.membresEspace')} emoji="👥"
            sousTitre={`${t(membres.length > 1 ? 'profil.nMembresPluriel' : 'profil.nMembresSingulier', { n: membres.length })}${invitations.length ? ` · ${t(invitations.length > 1 ? 'profil.nInvitePluriel' : 'profil.nInviteSingulier', { n: invitations.length })}` : ''}`}
            ouvert={ouvert === 'membres'} onToggle={() => setOuvert(o => (o === 'membres' ? null : 'membres'))}>
            {membres.map(m => (
              <View key={m.uid} style={s.ligne}>
                <View style={s.ligneTxt}>
                  <Text style={s.ligneEmoji}>{m.role === 'proprietaire' ? '👑' : '🙂'}</Text>
                  <View style={{ flex: 1 }}>
                    <Text style={s.ligneNom} numberOfLines={1}>{m.nom || m.uid.slice(0, 6)}{m.uid === user?.uid ? ` ${t('profil.moi')}` : ''}</Text>
                    {/* L'e-mail distingue les homonymes (même prénom, comptes différents) —
                        `m.email` peut être vide sur un vieux membre pas encore auto-réparé. */}
                    <Text style={s.ligneSous} numberOfLines={1}>
                      {m.email ? `${m.email} · ` : ''}{m.role === 'proprietaire' ? t('profil.proprietaire') : t('profil.membre')}
                    </Text>
                  </View>
                </View>
                {suisProprio && m.uid !== ownerUid && (
                  <TouchableOpacity hitSlop={HIT} style={s.mini} onPress={() => retirerMembre(espaceActif.id, m.uid).catch(e => setErr(e?.message || String(e)))}>
                    <Text style={s.miniTxt}>🗑️</Text>
                  </TouchableOpacity>
                )}
              </View>
            ))}
            {invitations.map(mail => (
              <View key={mail} style={[s.ligne, { opacity: 0.75 }]}>
                <View style={s.ligneTxt}>
                  <Text style={s.ligneEmoji}>✉️</Text>
                  <View style={{ flex: 1 }}>
                    <Text style={s.ligneNom} numberOfLines={1}>{mail}</Text>
                    <Text style={s.ligneSous}>{t('profil.inviteEnAttente')}</Text>
                  </View>
                </View>
                {suisProprio && (
                  <TouchableOpacity hitSlop={HIT} style={s.mini} onPress={() => annulerInvitation(espaceActif.id, mail).catch(() => {})}>
                    <Text style={s.miniTxt}>✕</Text>
                  </TouchableOpacity>
                )}
              </View>
            ))}
            {suisProprio && (
              <View style={s.inviteRow}>
                <TextInput style={[s.input, { flex: 1 }]} value={inviteMail} onChangeText={setInviteMail}
                  placeholder={t('profil.emailAInviter')} placeholderTextColor={UI.faint}
                  autoCapitalize="none" keyboardType="email-address" autoCorrect={false} />
                <TouchableOpacity style={[s.inviteBtn, !inviteMail.trim() && { opacity: 0.4 }]}
                  disabled={!inviteMail.trim()} onPress={inviter}>
                  <Text style={s.inviteBtnTxt}>{t('commun.inviter')}</Text>
                </TouchableOpacity>
              </View>
            )}
            <Text style={s.aide}>
              {t('profil.membresAide')}
            </Text>
          </SectionPliable>
        )}

        {/* ── Qui voit quoi ── (réservé au responsable de l'espace) ── */}
        {!!espaceActif && suisProprio && membres.length > 1 && (
          <SectionPliable
            titre={t('profil.quiVoitQuoi')} emoji="🔐"
            sousTitre={t('profil.accesParMembre')}
            ouvert={ouvert === 'acces'} onToggle={() => setOuvert(o => (o === 'acces' ? null : 'acces'))}>
            <Text style={s.aide}>
              {t('profil.quiVoitQuoiAide')}
            </Text>

            {/* Inventaires & plans : accès injecté par le module (appliqué jusqu'à Firestore). */}
            {sections.filter(x => x.acces).map(x => (
              <View key={x.cle} style={{ marginTop: 6 }}>
                <Text style={s.accGroupe}>{x.emoji} {x.titre}</Text>
                {x.rendu}
              </View>
            ))}

            {/* Finance (accès déclaré ; honoré quand le module finance arrivera).
                Même carte unifiée que les calendriers / inventaires / plans. */}
            <Text style={s.accGroupe}>💰 {t('profil.finance')}</Text>
            <SectionAcces titre={t('profil.budgetFinances')} emoji="💰" sousTitre={t('profil.budgetFinancesSous')}
              membres={membres} ownerUid={ownerUid}
              acces={accesDeclareN(espaceActif, 'finance')}
              onChange={(a) => definirDeclare('finance', a)} />
          </SectionPliable>
        )}

        {/* ── Mon forfait : plan en cours + demande de changement ── */}
        <SectionPliable
          titre={t('forfait.monForfait')} emoji="💳"
          sousTitre={nomPlan(plansCfg, user?.plan || 'gratuit')}
          ouvert={ouvert === 'forfait'} onToggle={() => setOuvert(o => (o === 'forfait' ? null : 'forfait'))}>
          <SectionMonForfait />
        </SectionPliable>

        {/* ── À la carte : fonctions et plafonds en plus du forfait ── */}
        <SectionPliable
          titre={t('forfait.aLaCarte')} emoji="🛒" sousTitre={t('forfait.aLaCarteSous')}
          ouvert={ouvert === 'alacarte'} onToggle={() => setOuvert(o => (o === 'alacarte' ? null : 'alacarte'))}>
          <SectionALaCarte />
        </SectionPliable>

        {/* ── Mes éléments : ce que j'utilise avec mes jetons ──────
            Une LIGNE (comme « Fonctions & activation ») : la page reste courte
            et le choix se fait dans une feuille dédiée. */}
        <TouchableOpacity style={s.carteLigne} activeOpacity={0.7} onPress={() => setElementsSheet(true)}>
          <View style={s.ligneTxt}>
            <Text style={s.ligneEmoji}>🎟️</Text>
            <View style={{ flex: 1 }}>
              <Text style={s.ligneNom}>{t('jetons.titre')}</Text>
              <Text style={s.ligneSous}>{t('jetons.sousTitre', { n: (user?.jetonsSelection || []).length })}</Text>
            </View>
          </View>
          <Text style={s.bascIc}>›</Text>
        </TouchableOpacity>

        {/* ── Apparence ──────────────────────────────────────────── */}
        {peutApparence && <SectionPliable
          titre={t('apparence.section')} emoji="🎨"
          sousTitre={t((THEMES.find(x => x.id === theme) || {}).labelKey)}
          ouvert={ouvert === 'apparence'} onToggle={() => setOuvert(o => (o === 'apparence' ? null : 'apparence'))}>
          <View style={s.puces}>
            {THEMES.map(x => (
              <TouchableOpacity key={x.id} style={[s.puce, theme === x.id && s.puceOn]} onPress={() => setTheme(x.id)}>
                <Text style={[s.puceTxt, theme === x.id && s.puceTxtOn]}>{x.emoji} {t(x.labelKey)}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </SectionPliable>}

        {/* ── Langue ─────────────────────────────────────────────── */}
        {peutLangue && !!langueCtx.changerLangue && (
          <SectionPliable
            titre={t('langue.titre')} emoji="🌐"
            sousTitre={(LANGUES.find(l => l.code === langueCtx.langue) || {}).label}
            ouvert={ouvert === 'langue'} onToggle={() => setOuvert(o => (o === 'langue' ? null : 'langue'))}>
            <View style={s.puces}>
              {LANGUES.map(l => (
                <TouchableOpacity key={l.code} style={[s.puce, langueCtx.langue === l.code && s.puceOn]}
                  onPress={() => langueCtx.changerLangue(l.code)}>
                  <Text style={[s.puceTxt, langueCtx.langue === l.code && s.puceTxtOn]}>{l.drapeau} {l.label}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </SectionPliable>
        )}

        {/* ── À propos ───────────────────────────────────────────── */}
        <SectionPliable
          titre={t('profil.aPropos')} emoji="ℹ️" sousTitre={`${app.nom} · v${app.version}`}
          ouvert={ouvert === 'apropos'} onToggle={() => setOuvert(o => (o === 'apropos' ? null : 'apropos'))}>
          {/* Nom + emoji seulement : les numeros sont dans la carte ci-dessous,
              une seule fois (le sous-titre de la section les repete deja). */}
          <Text style={s.apropos}>
            {appInfo(app.id).emoji} <Text style={{ fontWeight: '700' }}>{app.nom}</Text>
          </Text>
          <Text style={s.aide}>{t('profil.modulesComposant')}</Text>
          <View style={s.puces}>
            {app.modules.map(m => (
              <View key={m.id} style={s.puce}>
                <Text style={s.puceTxt}>
                  {m.emoji} {m.label}{m.mode === 'read' ? ` · ${t('profil.moduleLecture')}` : ''}
                </Text>
              </View>
            ))}
          </View>
          {/* Ce qu'on demande a un testeur des qu'il remonte quelque chose :
              quelle version tourne VRAIMENT, quel bundle, sur quel canal.
              Sans ca, il faut le lui faire chercher a la main. */}
          <CarteInfos style={{ marginTop: 12 }} rows={[
            { ic: '📦', lbl: t('profil.aproposVersion'), val: `v${app.version}` },
            { ic: '🧩', lbl: t('profil.aproposCore'), val: `v${CORE_VERSION}` },
            { ic: '🔄', lbl: t('profil.aproposEtat'),
              val: majDispo === null ? t('version.enAttente')
                 : majDispo ? `${t('version.mettreAJour')} (v${derniereVersion})`
                 : `${t('version.aJour')} ✓` },
            infoOTA.canal   && { ic: '🛤️', lbl: t('profil.aproposCanal'), val: infoOTA.canal },
            infoOTA.bundle  && { ic: '🆔', lbl: t('profil.aproposBundle'), val: infoOTA.bundle },
            { ic: '🔥', lbl: t('profil.aproposBase'), val: libelleProjet },
            { ic: '©', lbl: t('profil.aproposEditeur'), val: 'MoP Suite · 2026' },
          ].filter(Boolean)} />
        </SectionPliable>

        {/* ── Suppression de compte SELF-SERVICE (20/08) ────────────
            Exigence Google Play / Apple : un compte qui peut s'inscrire doit
            pouvoir se supprimer lui-même, dans l'app, sans passer par un
            administrateur. Passe par la corbeille (purge différée), pas une
            perte immédiate. */}
        <TouchableOpacity style={s.deco} activeOpacity={0.7} disabled={suppBusy} onPress={supprimerMonCompte}>
          <Text style={s.decoTxt}>{suppBusy ? '…' : t('profil.supprimerMonCompte')}</Text>
        </TouchableOpacity>

        {/* ── Gestion AUTRE : outils transverses (valideurs IVV, activation
            des fonctions) — ni spécifique à l'app, ni réglage courant. ── */}
        {estIVV && <Text style={s.titreGroupe}>{t('profil.gestionAutre')}</Text>}
        {/* ── Calendriers IVV (valideurs) : visibles ou non dans la liste ── */}
        {estIVV && (
          <View style={s.ligne}>
            <View style={s.ligneTxt}>
              <Text style={s.ligneEmoji}>🐞</Text>
              <View style={{ flex: 1 }}>
                <Text style={s.ligneTitre}>{t('profil.voirCalIvv')}</Text>
                <Text style={s.ligneSous}>{t('profil.voirCalIvvSous')}</Text>
              </View>
            </View>
            <Bascule valeur={!!user?.prefVoirIvv} accessibilityLabel={t('profil.voirCalIvv')}
              onChange={(v) => setDoc(doc(db, 'users', user.uid), { prefVoirIvv: v }, { merge: true }).catch(() => {})} />
          </View>
        )}
        {/* Choix PAR calendrier IVV : même présentation que « Fonctions &
            activation » — une LIGNE ouvre l'écran de sélection (la page reste
            courte). Sélection conservée quand tout est masqué. */}
        {estIVV && !!user?.prefVoirIvv && (
          <TouchableOpacity style={s.ligne} activeOpacity={0.7} onPress={() => setCalIvvSheet(true)}>
            <View style={s.ligneTxt}>
              <Text style={s.ligneEmoji}>🗓️</Text>
              <View style={{ flex: 1 }}>
                <Text style={s.ligneNom}>{t('profil.calIvvVisibles')}</Text>
                <Text style={s.ligneSous}>{t('profil.calIvvVisiblesSous')}</Text>
              </View>
            </View>
            <Text style={s.bascIc}>›</Text>
          </TouchableOpacity>
        )}

        {/* ── Surcharge IVV des fonctions (valideurs uniquement) ────
            Même schéma que « Voir les calendriers IVV » : une bascule active la
            surcharge, et SEULEMENT alors l'écran d'activation apparaît. Sans
            elle, ce sont les FORFAITS qui décident (cas de tout utilisateur). */}
        {/* UNE seule entree par fonction : la bascule d'activation vit sur la
            ligne de la fonction elle-meme, et le detail (ici l'ecran
            d'activation) n'apparait qu'une fois activee. Deux cartes pour un
            meme reglage obligeaient a faire le lien de tete. */}
        {estIVV && (
          <View style={s.bloc}>
            <View style={s.ligneNue}>
              <View style={s.ligneTxt}>
                <Text style={s.ligneEmoji}>🧩</Text>
                <View style={{ flex: 1 }}>
                  <Text style={s.ligneTitre}>{t('forfait.fonctionsActivation')}</Text>
                  <Text style={s.ligneSous}>{t('profil.surchargeFonctionsSous')}</Text>
                </View>
              </View>
              <Bascule valeur={!!user?.ivvSurchargeFonctions} accessibilityLabel={t('profil.surchargeFonctions')}
                onChange={(v) => setDoc(doc(db, 'users', user.uid), { ivvSurchargeFonctions: v }, { merge: true }).catch(() => {})} />
            </View>
            {!!user?.ivvSurchargeFonctions && fonctionsDefs.length > 0 && (
              <TouchableOpacity style={s.sousLigne} activeOpacity={0.7} onPress={() => setFonctionsSheet(true)}>
                <Text style={s.ligneNom}>{t('profil.fonctionsSous')}</Text>
                <Text style={s.bascIc}>›</Text>
              </TouchableOpacity>
            )}
          </View>
        )}
        {/* ── Jetons : un valideur teste avec la dotation d'un vrai forfait,
            mais peut la forcer pour explorer les cas limites (0 jeton, 10…). ── */}
        {estIVV && (
          <View style={s.bloc}>
            <View style={s.ligneNue}>
              <View style={s.ligneTxt}>
                <Text style={s.ligneEmoji}>🎟️</Text>
                <View style={{ flex: 1 }}>
                  <Text style={s.ligneTitre}>{t('profil.surchargeJetons')}</Text>
                  <Text style={s.ligneSous}>{t('profil.surchargeJetonsSous')}</Text>
                </View>
              </View>
              <Bascule valeur={!!user?.ivvSurchargeJetons} accessibilityLabel={t('profil.surchargeJetons')}
                onChange={(v) => setDoc(doc(db, 'users', user.uid), { ivvSurchargeJetons: v }, { merge: true }).catch(() => {})} />
            </View>
            {/* La coupure de limite EST une surcharge : elle vit ici, et quand
                elle est active les compteurs disparaissent — regler un quota
                qu'on vient de couper n'aurait aucun sens. */}
            {!!user?.ivvSurchargeJetons && (
              <View style={s.sousLigne}>
                <Text style={s.ligneNom}>🚫 {t('profil.sansLimiteJetons')}</Text>
                <Bascule valeur={!!user?.ivvSansLimiteJetons} accessibilityLabel={t('profil.sansLimiteJetons')}
                  onChange={(v) => setDoc(doc(db, 'users', user.uid), { ivvSansLimiteJetons: v }, { merge: true }).catch(() => {})} />
              </View>
            )}
            {!!user?.ivvSurchargeJetons && !user?.ivvSansLimiteJetons && (() => {
              // Point de depart = la dotation du PLAN, pour ne pas repartir de zero.
              const base = jetonsDuPlan(cfgFonctions, user?.plan || 'gratuit');
              const j = { ...base, ...(user?.jetons || {}) };
              const maj = (nature, n) => setDoc(doc(db, 'users', user.uid),
                { jetons: { ...j, [nature]: Math.max(0, n) } }, { merge: true }).catch(() => {});
              return (
                <>
                  <View style={s.sousLigne}>
                    <Text style={s.ligneNom}>⚙️ {t('admin.jetonsGestion')}</Text>
                    <Stepper valeur={j.gestion} onMoins={() => maj('gestion', j.gestion - 1)} onPlus={() => maj('gestion', j.gestion + 1)} />
                  </View>
                  <View style={s.sousLigne}>
                    <Text style={s.ligneNom}>👁 {t('admin.jetonsVue')}</Text>
                    <Stepper valeur={j.vue} onMoins={() => maj('vue', j.vue - 1)} onPlus={() => maj('vue', j.vue + 1)} />
                  </View>
                </>
              );
            })()}
          </View>
        )}

        <View style={{ height: 26 }} />
      </ScrollView>

      {/* ── Feuille espace (création / renommage) ─────────────────── */}
      <Feuille visible={!!sheet}
        titre={sheet?.id ? t('profil.modifierEspace') : t('profil.nouvelEspace')} emoji="🏠"
        onFermer={() => setSheet(null)}
        action={{ icone: sheet?.id ? '💾' : '＋', onPress: enregistrerEspace, disabled: !modifieEspace || busy, label: t('commun.enregistrer') }}>
        <Text style={s.lbl}>{t('listes.nom')}</Text>
        <TextInput style={s.input} value={sheet?.nom || ''}
          onChangeText={(v) => setSheet(x => ({ ...x, nom: v }))}
          placeholder={t('profil.nomEspacePlaceholder')} placeholderTextColor={UI.faint} />
        <Text style={s.lbl}>{t('profil.icone')}</Text>
        <SelecteurEmoji inline avecRecherche valeur={sheet?.emoji || '🏠'}
          onChange={(e) => setSheet(x => ({ ...x, emoji: e }))} />
      </Feuille>

      <BasculeComptes visible={bascule} onClose={() => setBascule(false)}
        user={user} onDeconnexion={() => deconnexion?.()} />

      <FeuilleParametresCompte visible={parametresCompte} uid={user?.uid} nomActuel={user?.prenom}
        onFermer={() => setParametresCompte(false)} />

      <FeuilleFonctions visible={fonctionsSheet} onFermer={() => setFonctionsSheet(false)} />
      <FeuilleMesElements visible={elementsSheet} sources={elementsSources} cfgFonctions={cfgFonctions}
        onFermer={() => setElementsSheet(false)} />
      {/* Fiche d'un espace : ce qu'il contient, tous types confondus. */}
      <FicheEspace espace={ficheEspace} sources={elementsSources}
        actif={espaceActif?.id === ficheEspace?.id}
        onFermer={() => setFicheEspace(null)}
        onRendreActif={() => { choisirEspace(ficheEspace.id); setFicheEspace(null); }}
        onEditer={roleDansEspace(ficheEspace, user?.uid) === 'proprietaire'
          ? () => { const e = ficheEspace; setFicheEspace(null);
              setSheet({ id: e.id, nom: e.nom, emoji: e.emoji || '🏠', nomInitial: e.nom, emojiInitial: e.emoji || '🏠' }); }
          : undefined} />
      <FeuilleCalIvv visible={calIvvSheet} user={user} onFermer={() => setCalIvvSheet(false)} />
    </View>
    </FormAccesProvider>
  );
}

const HIT = { top: 8, bottom: 8, left: 8, right: 8 };

const makeStyles = (UI) => StyleSheet.create({
  page:  { flex: 1, backgroundColor: UI.bg },
  corps: { padding: 16 },
  hIc:       { fontSize: 20, color: UI.accent, fontWeight: '600' },
  hIcDanger: { fontSize: 18, color: UI.danger },
  aideMini:  { fontSize: 11.5, color: UI.faint, marginTop: 4 },
  err:    { backgroundColor: UI.dangerTint, borderRadius: 10, borderWidth: 1, borderColor: UI.danger,
            padding: 11, marginBottom: 12 },
  errTxt: { color: UI.danger, fontSize: 12.5, fontWeight: '600' },
  // Bandeau d'identite fixe : separe du contenu par une bordure, comme une
  // barre — il n'appartient plus au flux qui defile.
  identiteFixe: { flexDirection: 'row', alignItems: 'center', gap: 12,
                  paddingHorizontal: 14, paddingVertical: 12,
                  backgroundColor: UI.bg, borderBottomWidth: 1, borderBottomColor: UI.border },
  identiteInfos: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 12 },
  avatar:    { width: 48, height: 48, borderRadius: 24, backgroundColor: UI.accent, alignItems: 'center', justifyContent: 'center' },
  avatarTxt: { color: '#fff', fontSize: 20, fontWeight: '800' },
  nom:   { fontSize: 17, fontWeight: '800', color: UI.ink },
  mail:  { fontSize: 12.5, color: UI.muted, marginTop: 1 },
  bascIc:{ fontSize: 20, color: UI.accent, fontWeight: '800', paddingHorizontal: 4 },
  bascIcOff: { color: UI.faint, fontSize: 15 },
  aide:  { fontSize: 12, color: UI.muted, lineHeight: 17, marginBottom: 11 },
  vide:  { fontSize: 13, color: UI.faint, marginBottom: 8 },
  ligne:      { flexDirection: 'row', alignItems: 'center', borderRadius: 10, borderWidth: 1,
                borderColor: UI.border, backgroundColor: UI.champ, paddingRight: 6, marginBottom: 7 },
  ligneOn:    { borderColor: UI.accent, backgroundColor: UI.accentTint },
  // Carte d'une fonction : sa bascule EN TETE, son detail replie dessous.
  bloc:       { borderRadius: 10, borderWidth: 1, borderColor: UI.border,
                backgroundColor: UI.champ, marginBottom: 7, overflow: 'hidden' },
  ligneNue:   { flexDirection: 'row', alignItems: 'center', paddingRight: 6 },
  sousLigne:  { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
                paddingLeft: 42, paddingRight: 10, paddingVertical: 9,
                borderTopWidth: 1, borderTopColor: UI.border },
  ligneTxt:   { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 10, padding: 11 },
  ligneEmoji: { fontSize: 20 },
  ligneNom:   { fontSize: 14.5, fontWeight: '700', color: UI.ink },
  ligneSous:  { fontSize: 11.5, color: UI.muted, marginTop: 1 },
  ligneTitre: { fontSize: 14.5, fontWeight: '700', color: UI.ink },
  carteLigne: { flexDirection: 'row', alignItems: 'center', backgroundColor: UI.card, borderRadius: 13,
                borderWidth: 0.5, borderColor: UI.border, paddingRight: 10, marginBottom: 10 },
  mini:    { padding: 6 },
  miniTxt: { fontSize: 15 },
  ajout:    { borderRadius: 10, borderWidth: 1, borderColor: UI.accent, borderStyle: 'dashed',
              paddingVertical: 11, alignItems: 'center', marginTop: 4 },
  ajoutTxt: { color: UI.accent, fontWeight: '700', fontSize: 13.5 },
  puces:   { flexDirection: 'row', flexWrap: 'wrap', gap: 7 },
  puce:    { borderWidth: 1, borderColor: UI.border, borderRadius: 9, paddingHorizontal: 11, paddingVertical: 7 },
  puceOn:  { borderColor: UI.accent, backgroundColor: UI.accentTint },
  puceTxt: { fontSize: 12.5, color: UI.muted, fontWeight: '600' },
  puceTxtOn: { color: UI.accent },
  apropos: { fontSize: 13.5, color: UI.ink, marginBottom: 11 },
  inviteRow:   { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 8 },
  inviteBtn:   { backgroundColor: UI.accent, borderRadius: 9, paddingHorizontal: 14, paddingVertical: 11 },
  inviteBtnTxt:{ color: '#fff', fontWeight: '700', fontSize: 13.5 },
  accGroupe:   { fontSize: 12, fontWeight: '800', color: UI.faint, textTransform: 'uppercase',
                 letterSpacing: 0.4, marginTop: 16, marginBottom: 2 },
  // Titres de REGROUPEMENT de la page (spécifique app / générique Core).
  titreGroupe: { fontSize: 12.5, fontWeight: '900', color: UI.muted, textTransform: 'uppercase',
                 letterSpacing: 0.6, marginTop: 18, marginBottom: 8, paddingHorizontal: 2 },
  deco:    { marginTop: 8, borderRadius: 11, borderWidth: 1, borderColor: UI.danger, paddingVertical: 12, alignItems: 'center' },
  decoTxt: { color: UI.danger, fontWeight: '700', fontSize: 14.5 },
  fond:    { flex: 1, backgroundColor: UI.overlay, justifyContent: 'flex-end' },
  feuille: { height: '82%', backgroundColor: UI.bg, borderTopLeftRadius: 18, borderTopRightRadius: 18, overflow: 'hidden' },
  feuilleCorps: { padding: 16 },
  lbl:   { fontSize: 12, fontWeight: '700', color: UI.muted, marginTop: 12, marginBottom: 7 },
  input: { backgroundColor: UI.champ, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 11,
           fontSize: 15, color: UI.ink, borderWidth: 0.5, borderColor: UI.champBord },
});
